import { useAuth0 } from "@auth0/auth0-react";
import { PropsWithChildren, useEffect } from "react";
import { z } from "zod";

import {
  readSessionValue,
  removeSessionValue,
  writeSessionValue,
} from "@punto/frontend-shared/utils/session-storage";
import { FullScreenLoader } from "@punto/ui/components/full-screen-loader";

import { AuthErrorScreen } from "./auth-error-screen";

/**
 * Records that this tab has already spent its one silent recovery. Session
 * scope on purpose: it has to survive the `logout` -> Auth0 -> origin ->
 * `/authorize` round trip (same tab, same origin, so it does), while a new tab
 * starts over with a fresh attempt.
 */
export const AUTH_ERROR_BOUNCE_KEY = "punto.auth-error-bounced";

const bouncedSchema = z.literal(true);

/**
 * Absorbs an Auth0 login that came back as an error instead of a session
 * (P-2726).
 *
 * Auth0 rejects a user who belongs to no organization and redirects to
 * `/callback?error=invalid_request&state=…`. `hasAuthParams()` matches that
 * as readily as a successful `code=`, so the provider runs
 * `handleRedirectCallback()`, it throws, and the provider stores the throw as
 * `error` with `isAuthenticated` still false. `withAuthenticationRequired` never looks at `error` — it only
 * asks `isLoading || isAuthenticated` — so it immediately calls
 * `loginWithRedirect` again, and because the Auth0 session itself is perfectly
 * alive (the identity provider login *succeeded*; only the client's
 * organization requirement failed) the next attempt fails in the same instant.
 * That is the infinite redirect loop, entered with no user interaction at all.
 *
 * This gate sits above everything that could start a login, so an error state
 * means nothing downstream renders to retry it. `error` is a precise signal: in
 * auth0-spa-js `checkSession()` swallows its own failures, so only a redirect
 * callback can set it — an ordinary first visit by an anonymous user never does.
 *
 * The recovery is `logout`, not another `loginWithRedirect`: dropping the Auth0
 * session is the only thing that makes the next attempt show the universal
 * login form rather than re-deciding against the session already there.
 *
 * It is spent once per tab. A silent retry that can itself fail is still a loop
 * — an identity provider that re-federates the same account without prompting
 * would put us straight back here — so the second failure stops and explains
 * instead, leaving the same recovery behind a button.
 */
export const AuthErrorGate = ({ children }: PropsWithChildren) => {
  const { error, isAuthenticated, logout } = useAuth0();

  // Read per render rather than held in state: the effect below writes it and
  // then navigates away, so no render depends on observing the write.
  const alreadyBounced =
    readSessionValue(AUTH_ERROR_BOUNCE_KEY, bouncedSchema) === true;

  // Reaching a session costs nothing and makes the next failure a fresh one.
  useEffect(() => {
    if (isAuthenticated) removeSessionValue(AUTH_ERROR_BOUNCE_KEY);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!error || alreadyBounced) return;

    writeSessionValue(AUTH_ERROR_BOUNCE_KEY, true);
    void logout({ logoutParams: { returnTo: window.location.origin } });
  }, [error, alreadyBounced, logout]);

  if (!error) return <>{children}</>;

  // The loader is only ever on screen for the moment it takes the effect's
  // `logout` redirect to leave the page.
  return alreadyBounced ? (
    <AuthErrorScreen error={error} />
  ) : (
    <FullScreenLoader />
  );
};
